/**
 * Task Storage Service
 *
 * Persists active task IDs to localStorage for recovery after page reload.
 * Tasks are retained for 24 hours after completion, then automatically cleaned up.
 */

import { TaskStatus, TaskType, isTaskTerminal } from '../types/task';

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'finbot_active_tasks';
const RETENTION_HOURS = 24;

// ============================================================================
// Types
// ============================================================================

/**
 * Stored task record for localStorage persistence
 */
export interface StoredTask {
  taskId: string;
  taskType: TaskType;
  namespace?: string;
  status: TaskStatus;
  progressPercentage: number;
  progressMessage?: string;
  createdAt: string;
  completedAt?: string;
  lastChecked?: string;
  /** Optional metadata for display purposes */
  metadata?: {
    name?: string;
    description?: string;
  };
}

// ============================================================================
// Storage Functions
// ============================================================================

/**
 * Get all stored tasks from localStorage
 */
export function getStoredTasks(): StoredTask[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];

    const tasks: StoredTask[] = JSON.parse(data);
    return Array.isArray(tasks) ? tasks : [];
  } catch (error) {
    console.error('[TaskStorage] Failed to parse stored tasks:', error);
    return [];
  }
}

/**
 * Save tasks to localStorage
 */
function saveTasks(tasks: StoredTask[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch (error) {
    console.error('[TaskStorage] Failed to save tasks:', error);
  }
}

/**
 * Save a new active task to storage
 */
export function saveActiveTask(task: Omit<StoredTask, 'createdAt' | 'lastChecked'>): void {
  const tasks = getStoredTasks();

  // Check if task already exists
  const existingIndex = tasks.findIndex((t) => t.taskId === task.taskId);

  const storedTask: StoredTask = {
    ...task,
    createdAt: existingIndex >= 0 ? tasks[existingIndex].createdAt : new Date().toISOString(),
    lastChecked: new Date().toISOString()
  };

  if (existingIndex >= 0) {
    // Update existing task
    tasks[existingIndex] = storedTask;
  } else {
    // Add new task
    tasks.push(storedTask);
  }

  saveTasks(tasks);
}

/**
 * Update task status in storage
 */
export function updateTaskStatus(taskId: string, status: TaskStatus, progressPercentage?: number, progressMessage?: string): void {
  const tasks = getStoredTasks();
  const taskIndex = tasks.findIndex((t) => t.taskId === taskId);

  if (taskIndex < 0) return;

  tasks[taskIndex] = {
    ...tasks[taskIndex],
    status,
    lastChecked: new Date().toISOString(),
    ...(progressPercentage !== undefined && { progressPercentage }),
    ...(progressMessage !== undefined && { progressMessage }),
    ...(isTaskTerminal(status) && { completedAt: new Date().toISOString() })
  };

  saveTasks(tasks);
}

/**
 * Remove a task from storage
 */
export function removeTask(taskId: string): void {
  const tasks = getStoredTasks();
  const filteredTasks = tasks.filter((t) => t.taskId !== taskId);
  saveTasks(filteredTasks);
}

/**
 * Get active (non-terminal) tasks
 */
export function getActiveTasks(): StoredTask[] {
  const tasks = getStoredTasks();
  return tasks.filter((t) => !isTaskTerminal(t.status));
}

/**
 * Get recently completed tasks (within retention period)
 */
export function getRecentCompletedTasks(): StoredTask[] {
  const tasks = getStoredTasks();
  const cutoff = Date.now() - RETENTION_HOURS * 60 * 60 * 1000;

  return tasks.filter((t) => {
    if (!isTaskTerminal(t.status)) return false;
    if (!t.completedAt) return false;
    return new Date(t.completedAt).getTime() > cutoff;
  });
}

/**
 * Cleanup expired tasks (older than retention period)
 * Should be called on app initialization
 */
export function cleanupExpiredTasks(): number {
  const tasks = getStoredTasks();
  const cutoff = Date.now() - RETENTION_HOURS * 60 * 60 * 1000;

  const validTasks = tasks.filter((task) => {
    // Keep active tasks
    if (!isTaskTerminal(task.status)) return true;

    // Keep completed tasks within retention period
    if (task.completedAt) {
      return new Date(task.completedAt).getTime() > cutoff;
    }

    // If no completedAt but terminal, use lastChecked or createdAt
    const timestamp = task.lastChecked || task.createdAt;
    return new Date(timestamp).getTime() > cutoff;
  });

  const removedCount = tasks.length - validTasks.length;

  if (removedCount > 0) {
    saveTasks(validTasks);
    console.log(`[TaskStorage] Cleaned up ${removedCount} expired tasks`);
  }

  return removedCount;
}

/**
 * Clear all stored tasks
 */
export function clearAllTasks(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Get task by ID
 */
export function getTaskById(taskId: string): StoredTask | undefined {
  const tasks = getStoredTasks();
  return tasks.find((t) => t.taskId === taskId);
}

/**
 * Check if a task exists in storage
 */
export function hasTask(taskId: string): boolean {
  return getTaskById(taskId) !== undefined;
}

/**
 * Get tasks by type
 */
export function getTasksByType(taskType: TaskType): StoredTask[] {
  const tasks = getStoredTasks();
  return tasks.filter((t) => t.taskType === taskType);
}

/**
 * Get tasks by namespace
 */
export function getTasksByNamespace(namespace: string): StoredTask[] {
  const tasks = getStoredTasks();
  return tasks.filter((t) => t.namespace === namespace);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format relative time for display (e.g., "2 phút trước")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 60) {
    return 'vừa xong';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} phút trước`;
  } else if (diffHours < 24) {
    return `${diffHours} giờ trước`;
  } else {
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} ngày trước`;
  }
}

/**
 * Get task type display name in Vietnamese
 */
export function getTaskTypeLabel(taskType: TaskType): string {
  const labels: Record<TaskType, string> = {
    kg_import: 'Nhập Knowledge Graph',
    kg_build: 'Xây dựng Knowledge Graph',
    kg_update: 'Cập nhật Knowledge Graph',
    kg_delete: 'Xóa Knowledge Graph',
    document_ingest: 'Nhập tài liệu',
    sync: 'Đồng bộ dữ liệu',
    ifc_web_sync: 'Đồng bộ IFC Web'
  };
  return labels[taskType] || taskType;
}
